import React, { useRef, useState } from 'react';
import { Alert, Card, Row, Col, Button, Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import PageTitle from '../../../layouts/PageTitle';
import { errorAlert } from '../../../components/alert/defaultAlert';
import { convertDoubleToStringPTBR } from '../../../../utils/ConverterUtils';
import { useBranch } from '../../../../context/BranchContext';
import PdfExportButton from '../../../components/Common/Report/PdfExportButton';
import PrintIcon from '../../../components/Icon/PrintIcon';
import DrePrintableReport from './DrePrintableReport';
import './DreReport.css';

const DreReport = () => {
    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const drePrintRef = useRef(null);
    const [reportData, setReportData] = useState(null);
    const [diagnosisData, setDiagnosisData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reportPeriod, setReportPeriod] = useState({
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD')
    });

    const safeNumber = (value) => Number(value || 0);
    const totalRevenue = safeNumber(reportData?.totals?.totalReceita);
    const totalCosts = safeNumber(reportData?.totals?.totalCustos);
    const totalExpenses = safeNumber(reportData?.totals?.totalDespesas);
    const grossProfit = safeNumber(reportData?.totals?.lucroBruto);
    const ebitda = safeNumber(reportData?.totals?.ebitda);
    const netResult = safeNumber(reportData?.totals?.lucroLiquido);
    const hasReportData = Boolean(reportData);
    const isInvalidPeriod = moment(reportPeriod.startDate).isAfter(moment(reportPeriod.endDate));
    const branchLabel = selectedBranch
        ? (selectedBranch.name || selectedBranch.description)
        : 'Relatório consolidado de todas as filiais';
    const periodLabel = `${moment(reportPeriod.startDate).format('DD/MM/YYYY')} - ${moment(reportPeriod.endDate).format('DD/MM/YYYY')}`;
    const activeQuickPeriod = (() => {
        const today = moment();
        const periods = {
            thisMonth: [today.clone().startOf('month'), today.clone().endOf('month')],
            lastMonth: [today.clone().subtract(1, 'month').startOf('month'), today.clone().subtract(1, 'month').endOf('month')],
            thisYear: [today.clone().startOf('year'), today.clone().endOf('year')],
            lastYear: [today.clone().subtract(1, 'year').startOf('year'), today.clone().subtract(1, 'year').endOf('year')]
        };

        return Object.keys(periods).find(key => (
            reportPeriod.startDate === periods[key][0].format('YYYY-MM-DD')
            && reportPeriod.endDate === periods[key][1].format('YYYY-MM-DD')
        ));
    })();
    const getRevenuePercent = (value) => {
        if (!totalRevenue) return '-';
        return `${((safeNumber(value) / totalRevenue) * 100).toLocaleString('pt-BR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        })}%`;
    };
    const getMarginPercent = (value) => {
        if (!totalRevenue) return '0,0%';
        return `${((safeNumber(value) / totalRevenue) * 100).toLocaleString('pt-BR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        })}%`;
    };
    const getValueClass = (value) => safeNumber(value) >= 0 ? 'text-success' : 'text-danger';

    // Percentuais do diagnóstico já vêm em pontos percentuais (ex.: 15.5 = 15,5%).
    // Retorna '-' quando o backend não pôde calcular (null), para não exibir "0,0%" enganoso.
    const formatPercentPoints = (value) => {
        if (value === null || value === undefined) return '-';
        return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    };
    // Variação com sinal explícito (+/-) para deixar claro se subiu ou caiu.
    const formatVariation = (value) => {
        if (value === null || value === undefined) return '-';
        const signal = Number(value) > 0 ? '+' : '';
        return `${signal}${formatPercentPoints(value)}`;
    };
    // Mapeia a severidade do backend para a aparência do card (cor/ícone).
    const severityMeta = {
        POSITIVO: { tone: 'success', icon: 'fa-circle-check' },
        ATENCAO: { tone: 'warning', icon: 'fa-triangle-exclamation' },
        CRITICO: { tone: 'danger', icon: 'fa-circle-exclamation' }
    };
    const getSeverityMeta = (severity) => severityMeta[severity] || severityMeta.ATENCAO;
    // Tom/seta da variação: verde+subindo, vermelho+caindo, cinza+neutro quando indefinida.
    const variationTone = (value) => (value === null || value === undefined) ? 'muted' : (Number(value) >= 0 ? 'success' : 'danger');
    const variationArrow = (value) => (value === null || value === undefined) ? 'fa-minus' : (Number(value) >= 0 ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down');
    const diagnosisIndicators = diagnosisData?.indicators;
    const diagnosisComparison = diagnosisData?.comparison;
    const diagnosisAlerts = diagnosisData?.alerts || [];
    const diagnosisOpportunity = diagnosisData?.topOpportunity;
    const hasDiagnosisData = Boolean(diagnosisData);
    const summaryCards = hasReportData ? [
        {
            key: 'revenue',
            icon: 'fa-coins',
            label: 'Receita Total',
            value: convertDoubleToStringPTBR(totalRevenue),
            detail: 'Base de cálculo do período',
            tone: 'success'
        },
        {
            key: 'costs',
            icon: 'fa-chart-line',
            label: 'Custos',
            value: convertDoubleToStringPTBR(Math.abs(totalCosts)),
            detail: getRevenuePercent(Math.abs(totalCosts)),
            tone: 'warning'
        },
        {
            key: 'expenses',
            icon: 'fa-credit-card',
            label: 'Despesas',
            value: convertDoubleToStringPTBR(Math.abs(totalExpenses)),
            detail: getRevenuePercent(Math.abs(totalExpenses)),
            tone: 'danger'
        },
        {
            key: 'net',
            icon: netResult >= 0 ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down',
            label: 'Resultado Líquido',
            value: convertDoubleToStringPTBR(netResult),
            detail: `Margem ${getMarginPercent(netResult)}`,
            tone: netResult >= 0 ? 'success' : 'danger'
        }
    ] : [];
    const indicatorCards = hasReportData ? [
        {
            key: 'grossProfit',
            label: 'Lucro Bruto',
            value: convertDoubleToStringPTBR(grossProfit),
            detail: `Margem bruta ${getMarginPercent(grossProfit)}`,
            icon: 'fa-layer-group',
            tone: grossProfit >= 0 ? 'success' : 'danger'
        },
        {
            key: 'ebitda',
            label: 'EBITDA',
            value: convertDoubleToStringPTBR(ebitda),
            detail: `Margem EBITDA ${getMarginPercent(ebitda)}`,
            icon: 'fa-chart-bar',
            tone: ebitda >= 0 ? 'success' : 'danger'
        },
        {
            key: 'netMargin',
            label: 'Margem Líquida',
            value: getMarginPercent(netResult),
            detail: convertDoubleToStringPTBR(netResult),
            icon: 'fa-percent',
            tone: netResult >= 0 ? 'success' : 'danger'
        }
    ] : [];

    const generateReport = async () => {
        if (isInvalidPeriod) {
            errorAlert('A data inicial precisa ser menor ou igual à data final.');
            return;
        }

        setLoading(true);
        try {
            const params = {
                startDate: reportPeriod.startDate,
                endDate: reportPeriod.endDate
            };

            if (selectedBranch && selectedBranch.id) {
                params.branchId = selectedBranch.id;
            }

            // Busca relatório e diagnóstico em paralelo — ambos usam o mesmo período/filial.
            // O diagnóstico não pode derrubar o relatório: se falhar, seguimos só com o DRE.
            const [reportResponse, diagnosisResponse] = await Promise.all([
                axiosInstance.get(`${Api.cash_flow.dre}/report`, { params }),
                axiosInstance.get(`${Api.cash_flow.dre}/diagnosis`, { params })
                    .catch((error) => {
                        console.error('Erro ao gerar diagnóstico DRE:', error);
                        return null;
                    })
            ]);

            setReportData(reportResponse.data);
            setDiagnosisData(diagnosisResponse ? diagnosisResponse.data : null);
        } catch (error) {
            console.error('Erro ao gerar relatório DRE:', error);
            errorAlert(error);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickPeriod = (type) => {
        const today = moment();
        let start, end;

        switch (type) {
            case 'thisMonth':
                start = today.clone().startOf('month');
                end = today.clone().endOf('month');
                break;
            case 'lastMonth':
                start = today.clone().subtract(1, 'month').startOf('month');
                end = today.clone().subtract(1, 'month').endOf('month');
                break;
            case 'thisYear':
                start = today.clone().startOf('year');
                end = today.clone().endOf('year');
                break;
            case 'lastYear':
                start = today.clone().subtract(1, 'year').startOf('year');
                end = today.clone().subtract(1, 'year').endOf('year');
                break;
            default:
                return;
        }

        setReportPeriod({
            startDate: start.format('YYYY-MM-DD'),
            endDate: end.format('YYYY-MM-DD')
        });
    };

    const renderDreGroup = (group) => {
        if (!group.items || group.items.length === 0) return null;

        const isPositive = (group.totalValue || 0) >= 0;
        const rowTone = group.groupValue === 'RECEITA' ? 'success' :
                        group.groupValue?.includes('CUSTO') || group.groupValue?.includes('DESPESA') ? 'warning' :
                        'primary';

        return (
            <React.Fragment key={group.groupName}>
                <tr className={`dre-group-row dre-group-row--${rowTone}`}>
                    <td colSpan="2">
                        <strong className="dre-group-title">
                            <i className={`fas ${getGroupIcon(group.groupValue)} me-2`}></i>
                            {group.groupName}
                        </strong>
                    </td>
                    <td className="text-end">
                        <strong>{getRevenuePercent(group.totalValue)}</strong>
                    </td>
                    <td className="text-end">
                        <strong className={isPositive ? 'text-success' : 'text-danger'}>
                            {convertDoubleToStringPTBR(group.totalValue || 0)}
                        </strong>
                    </td>
                </tr>
                {group.items.map((item, index) => (
                    <tr className="dre-item-row" key={index}>
                        <td>
                            <div className="dre-category-cell">
                                <span className="dre-category-marker"></span>
                                <span>{item.categoryName}</span>
                            </div>
                        </td>
                        <td className="text-center text-muted">
                            <span className="dre-count-pill">
                                {item.itemCount || 0} item(s)
                            </span>
                        </td>
                        <td className="text-end text-muted">
                            <small>{getRevenuePercent(item.netValue)}</small>
                        </td>
                        <td className="text-end">
                            <span className={getValueClass(item.netValue)}>
                                {convertDoubleToStringPTBR(item.netValue || 0)}
                            </span>
                        </td>
                    </tr>
                ))}
            </React.Fragment>
        );
    };

    // Função para determinar ícones por grupo
    const getGroupIcon = (groupValue) => {
        const icons = {
            'RECEITA': 'fa-coins',
            'IMPOSTO': 'fa-university',
            'CUSTO_VARIAVEL': 'fa-chart-line',
            'DESPESA_FIXA': 'fa-home',
            'ADMINISTRATIVA': 'fa-briefcase',
            'FINANCEIRA': 'fa-credit-card',
            'NAO_OPERACIONAL': 'fa-exclamation-triangle',
            'PRO_LABORE': 'fa-user-tie',
            'INVESTIMENTO': 'fa-seedling',
            'DEPRECIACAO': 'fa-arrow-down'
        };
        return icons[groupValue] || 'fa-folder';
    };

    return (
        <>
            <PageTitle
                activeMenu={t('cash_flow.dre.report')}
                motherMenu={t('cash_flow.label')}
            />

            <div className="dre-report-page">
                <Card className="dre-panel dre-filter-panel border-0">
                    <Card.Body>
                        <div className="dre-section-heading">
                            <div>
                                <h3 className="dre-section-title">Filtros do relatório</h3>
                                <p className="dre-section-subtitle mb-0">Defina o intervalo e use atalhos rápidos para trocar o período com menos esforço.</p>
                            </div>
                        </div>

                        {isInvalidPeriod && (
                            <Alert variant="warning" className="dre-period-alert">
                                <i className="fas fa-triangle-exclamation me-2"></i>
                                A data inicial precisa ser menor ou igual à data final.
                            </Alert>
                        )}

                        <Row className="g-3 align-items-end">
                            <Col xl={3} md={6}>
                                <Form.Group>
                                    <Form.Label>{t('date_start')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={reportPeriod.startDate}
                                        onChange={(e) => setReportPeriod(prev => ({
                                            ...prev,
                                            startDate: e.target.value
                                        }))}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xl={3} md={6}>
                                <Form.Group>
                                    <Form.Label>{t('date_end')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={reportPeriod.endDate}
                                        onChange={(e) => setReportPeriod(prev => ({
                                            ...prev,
                                            endDate: e.target.value
                                        }))}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xl={6}>
                                <div className="dre-quick-periods">
                                    <Button variant={activeQuickPeriod === 'thisMonth' ? 'primary' : 'outline-secondary'} onClick={() => handleQuickPeriod('thisMonth')}>
                                        <i className="fas fa-calendar-day me-1"></i>
                                        Este Mês
                                    </Button>
                                    <Button variant={activeQuickPeriod === 'lastMonth' ? 'primary' : 'outline-secondary'} onClick={() => handleQuickPeriod('lastMonth')}>
                                        <i className="fas fa-calendar-minus me-1"></i>
                                        Mês Anterior
                                    </Button>
                                    <Button variant={activeQuickPeriod === 'thisYear' ? 'primary' : 'outline-secondary'} onClick={() => handleQuickPeriod('thisYear')}>
                                        <i className="fas fa-calendar-alt me-1"></i>
                                        Este Ano
                                    </Button>
                                    <Button variant={activeQuickPeriod === 'lastYear' ? 'primary' : 'outline-secondary'} onClick={() => handleQuickPeriod('lastYear')}>
                                        <i className="fas fa-calendar me-1"></i>
                                        Ano Anterior
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        <div className="dre-filter-actions">
                            <Button
                                variant="primary"
                                onClick={generateReport}
                                disabled={loading || isInvalidPeriod}
                                className="dre-primary-action"
                            >
                                {loading ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        {t('loading')}
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-chart-line me-2"></i>
                                        {t('cash_flow.dre.generate_report')}
                                    </>
                                )}
                            </Button>
                        </div>

                        <div className="dre-branch-banner">
                            <i className={`fas ${selectedBranch ? 'fa-building' : 'fa-globe'} me-2`}></i>
                            <div>
                                <strong>Escopo atual:</strong> {branchLabel}
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {hasReportData && (
                    <div className="dre-print-source" aria-hidden="true">
                        {/* Fonte exclusiva do PDF: mantém a exportação estável mesmo se o layout da tela mudar. */}
                        <div ref={drePrintRef}>
                            <DrePrintableReport
                                reportData={reportData}
                                reportPeriod={reportPeriod}
                                selectedBranch={selectedBranch}
                                totalRevenue={totalRevenue}
                                totalExpenses={totalExpenses}
                                grossProfit={grossProfit}
                                ebitda={ebitda}
                                netResult={netResult}
                                getRevenuePercent={getRevenuePercent}
                                getMarginPercent={getMarginPercent}
                                getValueClass={getValueClass}
                            />
                        </div>
                    </div>
                )}

                {hasReportData && (
                    <>
                        <Row className="g-3 dre-summary-grid">
                            {summaryCards.map((card) => (
                                <Col xl={3} md={6} key={card.key}>
                                    <Card className={`dre-summary-card dre-summary-card--${card.tone} border-0`}>
                                        <Card.Body>
                                            <div className="dre-summary-icon">
                                                <i className={`fas ${card.icon}`}></i>
                                            </div>
                                            <span className="dre-summary-label">{card.label}</span>
                                            <strong className="dre-summary-value">{card.value}</strong>
                                            <span className="dre-summary-detail">{card.detail}</span>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        <Row className="g-3 dre-indicator-grid">
                            {indicatorCards.map((indicator) => (
                                <Col lg={4} key={indicator.key}>
                                    <div className="dre-indicator-card">
                                        <div className="dre-indicator-icon">
                                            <i className={`fas ${indicator.icon}`}></i>
                                        </div>
                                        <div>
                                            <span>{indicator.label}</span>
                                            <strong className={`text-${indicator.tone}`}>
                                                {indicator.value}
                                            </strong>
                                            <small>{indicator.detail}</small>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {hasDiagnosisData && (
                            <Card className="dre-panel dre-diagnosis-panel border-0">
                                <Card.Body>
                                    <div className="dre-section-heading">
                                        <div>
                                            <h3 className="dre-section-title mb-1">
                                                <i className="fas fa-stethoscope me-2"></i>
                                                Diagnóstico financeiro
                                            </h3>
                                            <p className="dre-section-subtitle mb-0">
                                                Comparação com o período anterior, ponto de equilíbrio e alertas automáticos do resultado.
                                            </p>
                                        </div>
                                    </div>

                                    <Row className="g-3 dre-diagnosis-metrics">
                                        {diagnosisComparison && (
                                            <>
                                                <Col md={4}>
                                                    <div className={`dre-diagnosis-metric dre-diagnosis-metric--${variationTone(diagnosisComparison.variacaoFaturamento)}`}>
                                                        <span className="dre-diagnosis-metric-label">
                                                            <i className={`fas ${variationArrow(diagnosisComparison.variacaoFaturamento)} me-2`}></i>
                                                            Faturamento
                                                        </span>
                                                        <strong>{formatVariation(diagnosisComparison.variacaoFaturamento)}</strong>
                                                        <small>
                                                            {convertDoubleToStringPTBR(diagnosisComparison.faturamentoAtual)} vs {convertDoubleToStringPTBR(diagnosisComparison.faturamentoAnterior)}
                                                            {diagnosisComparison.previousPeriod ? ` (${diagnosisComparison.previousPeriod})` : ''}
                                                        </small>
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div className={`dre-diagnosis-metric dre-diagnosis-metric--${variationTone(diagnosisComparison.variacaoLucroLiquido)}`}>
                                                        <span className="dre-diagnosis-metric-label">
                                                            <i className={`fas ${variationArrow(diagnosisComparison.variacaoLucroLiquido)} me-2`}></i>
                                                            Lucro líquido
                                                        </span>
                                                        <strong>{formatVariation(diagnosisComparison.variacaoLucroLiquido)}</strong>
                                                        <small>
                                                            {convertDoubleToStringPTBR(diagnosisComparison.lucroLiquidoAtual)} vs {convertDoubleToStringPTBR(diagnosisComparison.lucroLiquidoAnterior)}
                                                        </small>
                                                    </div>
                                                </Col>
                                            </>
                                        )}
                                        {diagnosisIndicators && diagnosisIndicators.pontoEquilibrio !== null && diagnosisIndicators.pontoEquilibrio !== undefined && (
                                            <Col md={4}>
                                                <div className="dre-diagnosis-metric dre-diagnosis-metric--info">
                                                    <span className="dre-diagnosis-metric-label">
                                                        <i className="fas fa-scale-balanced me-2"></i>
                                                        Ponto de equilíbrio
                                                    </span>
                                                    <strong>{convertDoubleToStringPTBR(diagnosisIndicators.pontoEquilibrio)}</strong>
                                                    <small>Faturamento mínimo para não ter prejuízo</small>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>

                                    {diagnosisOpportunity && (
                                        <div className="dre-opportunity">
                                            <div className="dre-opportunity-icon">
                                                <i className="fas fa-bullseye"></i>
                                            </div>
                                            <div className="dre-opportunity-content">
                                                <span className="dre-opportunity-kicker">Maior alavanca de melhoria</span>
                                                <strong className="dre-opportunity-title">
                                                    {diagnosisOpportunity.category}
                                                    {diagnosisOpportunity.percentOfRevenue !== null && diagnosisOpportunity.percentOfRevenue !== undefined
                                                        ? ` · ${formatPercentPoints(diagnosisOpportunity.percentOfRevenue)} da receita`
                                                        : ''}
                                                    {` · ${convertDoubleToStringPTBR(diagnosisOpportunity.amount)}`}
                                                </strong>
                                                {diagnosisOpportunity.recommendation && (
                                                    <p className="mb-0">
                                                        <i className="fas fa-hand-point-right me-2"></i>
                                                        {diagnosisOpportunity.recommendation}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {diagnosisAlerts.length > 0 ? (
                                        <div className="dre-alert-list">
                                            {diagnosisAlerts.map((alert, index) => {
                                                const meta = getSeverityMeta(alert.severity);
                                                return (
                                                    <div key={index} className={`dre-alert-item dre-alert-item--${meta.tone}`}>
                                                        <div className="dre-alert-icon">
                                                            <i className={`fas ${meta.icon}`}></i>
                                                        </div>
                                                        <div className="dre-alert-content">
                                                            <div className="dre-alert-head">
                                                                <strong>{alert.title}</strong>
                                                                {alert.category && <span className="dre-alert-tag">{alert.category}</span>}
                                                            </div>
                                                            <p className="mb-0">{alert.message}</p>
                                                            {alert.recommendation && (
                                                                <p className="dre-alert-recommendation mb-0">
                                                                    <i className="fas fa-hand-point-right me-2"></i>
                                                                    {alert.recommendation}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <Alert variant="success" className="mb-0">
                                            <i className="fas fa-circle-check me-2"></i>
                                            Nenhum alerta no período. Os indicadores estão dentro dos parâmetros saudáveis.
                                        </Alert>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        <Row className="g-3">
                            <Col xs={12}>
                                <Card className="dre-panel dre-results-panel border-0">
                                    <Card.Body>
                                        <div className="dre-section-heading dre-section-heading--results">
                                            <div>
                                                <h3 className="dre-section-title mb-1">{t('cash_flow.dre.title')}</h3>
                                                <p className="dre-section-subtitle mb-0">
                                                    {periodLabel} · {selectedBranch ? `Filial: ${branchLabel}` : 'Todas as filiais'}
                                                </p>
                                            </div>
                                            <PdfExportButton
                                                contentRef={drePrintRef}
                                                documentTitle={`dre-${reportPeriod.startDate}-${reportPeriod.endDate}`}
                                                className="d-print-none"
                                                disabled={!hasReportData}
                                            >
                                                <PrintIcon className="me-2" />
                                                PDF
                                            </PdfExportButton>
                                        </div>

                                        <Table responsive className="dre-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '45%' }}>{t('cash_flow.category')}</th>
                                                    <th style={{ width: '18%' }} className="text-center">Qtd. Itens</th>
                                                    <th style={{ width: '17%' }} className="text-end">% Receita</th>
                                                    <th style={{ width: '20%' }} className="text-end">{t('amount')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.groups && reportData.groups.map(group => renderDreGroup(group))}

                                                <tr className="dre-indicator-heading">
                                                    <td colSpan="4" className="text-center">
                                                        <strong><i className="fas fa-calculator me-2"></i>Indicadores Financeiros</strong>
                                                    </td>
                                                </tr>

                                                <tr className="dre-total-row dre-total-row--revenue">
                                                    <td colSpan="2">
                                                        <strong>
                                                            <i className="fas fa-coins me-2 text-success"></i>
                                                            {t('cash_flow.dre.total_revenue')}
                                                        </strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong>{getRevenuePercent(totalRevenue)}</strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong className="text-success">
                                                            {convertDoubleToStringPTBR(totalRevenue)}
                                                        </strong>
                                                    </td>
                                                </tr>

                                                <tr className="dre-total-row">
                                                    <td colSpan="2">
                                                        <strong>
                                                            <i className="fas fa-chart-line me-2 text-warning"></i>
                                                            Lucro Bruto
                                                        </strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong>{getMarginPercent(grossProfit)}</strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong className={getValueClass(grossProfit)}>
                                                            {convertDoubleToStringPTBR(grossProfit)}
                                                        </strong>
                                                    </td>
                                                </tr>

                                                <tr className="dre-total-row">
                                                    <td colSpan="2">
                                                        <strong>
                                                            <i className="fas fa-chart-bar me-2 text-info"></i>
                                                            EBITDA
                                                        </strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong>{getMarginPercent(ebitda)}</strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong className={getValueClass(ebitda)}>
                                                            {convertDoubleToStringPTBR(ebitda)}
                                                        </strong>
                                                    </td>
                                                </tr>

                                                <tr className="dre-total-row dre-total-row--expense">
                                                    <td colSpan="2">
                                                        <strong>
                                                            <i className="fas fa-credit-card me-2 text-danger"></i>
                                                            {t('cash_flow.dre.total_expenses')}
                                                        </strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong>{getRevenuePercent(Math.abs(totalExpenses))}</strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong className="text-danger">
                                                            {convertDoubleToStringPTBR(Math.abs(totalExpenses))}
                                                        </strong>
                                                    </td>
                                                </tr>

                                                <tr className={`dre-net-row dre-net-row--${netResult >= 0 ? 'positive' : 'negative'}`}>
                                                    <td colSpan="2">
                                                        <strong className="dre-net-result-label">
                                                            <i className={`fas ${netResult >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'} me-2`}></i>
                                                            {t('cash_flow.dre.net_result')}
                                                        </strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong>{getMarginPercent(netResult)}</strong>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong className={`dre-net-result-value ${getValueClass(netResult)}`}>
                                                            {convertDoubleToStringPTBR(netResult)}
                                                        </strong>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>

                                        <div className="dre-generated-at text-muted d-print-none">
                                            <small>
                                                <i className="fas fa-info-circle me-1"></i>
                                                Relatório gerado em {moment().format('DD/MM/YYYY HH:mm')}
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}

                {!hasReportData && (
                    <Card className="dre-panel dre-empty-state border-0">
                        <Card.Body>
                            <div className="text-center py-5">
                                <div className="mb-4">
                                    <i className="fas fa-chart-line fa-4x text-muted mb-3"></i>
                                </div>
                                <h4 className="text-muted mb-3">Relatório DRE</h4>
                                <p className="text-muted mb-4">
                                    Defina o período e gere o relatório para visualizar receitas, custos, despesas e resultado líquido em um único painel.
                                </p>
                                <div className="dre-empty-chips">
                                    <div className="badge bg-light text-dark p-2">
                                        <i className="fas fa-coins me-1"></i>
                                        Receitas
                                    </div>
                                    <div className="badge bg-light text-dark p-2">
                                        <i className="fas fa-chart-line me-1"></i>
                                        Custos
                                    </div>
                                    <div className="badge bg-light text-dark p-2">
                                        <i className="fas fa-credit-card me-1"></i>
                                        Despesas
                                    </div>
                                    <div className="badge bg-light text-dark p-2">
                                        <i className="fas fa-calculator me-1"></i>
                                        Resultado
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </>
    );
};

export default DreReport;
